import React from "react";
import type { User } from "../UsuariosPageNew";
import { getInitials } from "../UsuariosPageNew";

interface UserCardProps {
    user: User;
    onUserClick: (user: User) => void;
    onActionClick: (user: User, type: "status" | "superhost", e?: React.MouseEvent) => void;
}

const UserCard: React.FC<UserCardProps> = ({ user, onUserClick, onActionClick }) => {
    const isHostBlocked = user.blockedAsHost === true;
    const isGuestBlocked = user.blockedAsGuest === true;
    const hasBlocks = isHostBlocked || isGuestBlocked;

    // Si status es distinto de "AC" (por ejemplo, si en la lista viene "Unable")
    const isGlobalActive = user.status === "AC";

    // Un usuario está visualmente "suspendido" si tiene bloqueos de rol o su estado no es activo
    const isSuspendedUI = hasBlocks || !isGlobalActive;

    // 2. Lógica de roles
    const isHost = user.roles ? user.roles.includes("Host") : user.role === "Host";
    const isSuperHost = user.isSuperHost === true;

    // Helper para formatear el texto de la etiqueta de suspensión
    const getSuspensionLabelText = () => {
        if (!isGlobalActive && !hasBlocks) return "Inactivo"; // Para los que vienen con estado "Unable"
        if (isHostBlocked && isGuestBlocked) return "Suspendido";
        if (isHostBlocked) return "Anfitrión Susp.";
        if (isGuestBlocked) return "Huésped Susp.";
        return "Activo";
    };

    return (
        <div
            onClick={() => onUserClick(user)}
            className={`bg-white border rounded-xl p-[18px] cursor-pointer transition-all duration-200 relative flex flex-col h-full gap-3 group
                ${isSuperHost ? "border-amber-200 shadow-[0_0_0_1px_rgba(251,191,36,0.2)] hover:border-amber-400" : "border-gray-200 hover:border-[#4f6df5]"} 
                ${isSuspendedUI ? "bg-red-50/20 hover:border-red-300" : "hover:-translate-y-0.5 hover:shadow-lg"}
            `}
        >
            {isSuperHost && (
                <div
                    className="absolute top-3 right-3 flex items-center bg-gradient-to-br from-amber-400 to-amber-600 text-white py-1 px-2 rounded-md shadow-sm cursor-default group/sh transition-all duration-300 z-10"
                    title="Super Anfitrión"
                >
                    <span className="text-[11px] shrink-0 leading-none">⭐</span>

                    {/* Texto oculto que se revela */}
                    <span className="text-[10px] font-bold tracking-wider overflow-hidden whitespace-nowrap opacity-0 max-w-0 group-hover/sh:max-w-[120px] group-hover/sh:opacity-100 group-hover/sh:ml-1.5 transition-all duration-300 leading-none mt-[1px]">
                        Super Anfitrión
                    </span>
                </div>
            )}

            <div className="flex items-start gap-3 mt-1">
                <div className="w-12 h-12 rounded-full shrink-0 flex items-center justify-center text-[17px] font-bold text-white relative overflow-hidden bg-gradient-to-br from-[#4f6df5] to-[#2563eb]">
                    {user.photoLink ? (
                        <img src={user.photoLink} alt={user.name} className="w-full h-full object-cover" />
                    ) : (
                        getInitials(user.name, user.lastName)
                    )}

                    {/* Indicador de estado en la foto de perfil */}
                    <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white z-10 ${isSuspendedUI ? "bg-red-500" : "bg-emerald-500"
                        }`}></div>
                </div>
                <div className="flex-1 min-w-0 pr-6">
                    <div className="text-sm font-bold text-gray-900 leading-tight line-clamp-2 break-words">
                        {user.name} {user.lastName}
                    </div>
                    <div className="text-[11px] text-gray-500 leading-tight mt-0.5 line-clamp-2 break-words">
                        {user.email || "Sin correo visible"}
                    </div>

                    {/* Contenedor de Etiquetas (Roles y Estado) */}
                    <div className="flex gap-1.5 mt-1.5 flex-wrap">

                        {/* Etiquetas de Roles */}
                        {user.roles && user.roles.length > 0 ? (
                            user.roles
                                .filter(r => r === "Host" || r === "Guest")
                                .map((rol, idx) => {
                                    // Verificamos el booleano exacto dependiendo de la etiqueta que estamos dibujando
                                    const isThisRoleBlocked = (rol === "Host" && isHostBlocked) || (rol === "Guest" && isGuestBlocked);

                                    return (
                                        <span
                                            key={idx}
                                            className={`whitespace-nowrap text-[9px] font-bold px-[7px] py-[2px] rounded-md uppercase tracking-wider border 
                                                ${isThisRoleBlocked
                                                    ? "bg-red-50 text-red-600 border-red-200 line-through decoration-red-400/50"
                                                    : rol === "Host"
                                                        ? "bg-purple-50 text-purple-600 border-purple-100"
                                                        : "bg-blue-50 text-blue-600 border-blue-100"
                                                }`}
                                        >
                                            {rol === "Host" ? "Anfitrión" : "Huésped"}
                                        </span>
                                    );
                                })
                        ) : (
                            <span className={`whitespace-nowrap text-[9px] font-bold px-[7px] py-[2px] rounded-md uppercase tracking-wider border ${isHost ? "bg-purple-50 text-purple-600 border-purple-100" : "bg-blue-50 text-blue-600 border-blue-100"}`}>
                                {user.roleLabel || (isHost ? "Anfitrión" : "Huésped")}
                            </span>
                        )}

                        {/* Etiqueta de Estado General */}
                        <span className={`whitespace-nowrap text-[9px] font-bold px-[7px] py-[2px] rounded-md uppercase tracking-wider border ${!isSuspendedUI ? "bg-emerald-50 text-emerald-600 border-emerald-100" : "bg-red-50 text-red-600 border-red-100"}`}>
                            {getSuspensionLabelText()}
                        </span>
                    </div>
                </div>
            </div>

            {/* 4. BLOQUE INFERIOR */}
            <div className="mt-auto flex flex-col">
                <div className="h-px bg-gray-100 mt-4 mb-3"></div>

                <div className="flex justify-between mb-3">
                    <div className="text-center">
                        <div className="font-mono text-sm font-bold text-[#4f6df5]">{user.reservas}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5">Reservas</div>
                    </div>
                    <div className="text-center">
                        <div className="font-mono text-sm font-bold text-emerald-600">${user.monto?.toLocaleString()}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5">{user.tipoMonto}</div>
                    </div>
                    <div className="text-center">
                        <div className="font-mono text-sm font-bold text-amber-500">{user.rating ? `${user.rating} ★` : '—'}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5">Rating</div>
                    </div>
                </div>

                <div className="flex gap-1.5">
                    <button className="flex-1 py-1.5 border border-gray-200 bg-white text-gray-600 rounded-md text-[11px] font-semibold transition-colors hover:border-[#4f6df5] hover:text-[#4f6df5] hover:bg-blue-50 shadow-sm">
                        Ver perfil
                    </button>
                    {/* Lógica del botón Super Anfitrión vs Suspender/Reactivar */}
                    {isHost && !isHostBlocked && !isSuperHost ? (
                        <button
                            className="flex-1 py-1.5 border border-gray-200 bg-white text-gray-600 rounded-md text-[11px] font-semibold transition-colors hover:border-amber-400 hover:text-amber-600 hover:bg-amber-50 shadow-sm"
                            onClick={(e) => onActionClick(user, "superhost", e)}
                        >
                            ⭐ Super Anf.
                        </button>
                    ) : (
                        <button
                            className={`flex-1 py-1.5 border border-gray-200 bg-white text-gray-600 rounded-md text-[11px] font-semibold transition-colors shadow-sm 
                                ${!isSuspendedUI
                                    ? "hover:border-red-300 hover:text-red-600 hover:bg-red-50"
                                    : "hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50"
                                }`}
                            onClick={(e) => onActionClick(user, "status", e)}
                        >
                            {!isSuspendedUI ? "Suspender" : "Reactivar"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserCard;